'use client'

import { QueryCallbacks } from '@/core/lib/utils/create-query'
import { debounce } from '@/core/lib/utils/debounce'
import { ID } from '@/core/types/models/id.model'
import { AirportModel } from '@/features/locations/types/models/airport.model'
import axios from 'axios'
import { useMemo, useRef } from 'react'
import { BookFormModel } from '../../../types/models/book-form.model'

type CompareAirportModel = {
  latitude: string | number
  longitude: string | number
}

export const compareAirports = (
  a: CompareAirportModel,
  b: CompareAirportModel,
) => a.latitude !== b.latitude || a.longitude !== b.longitude

export const filterAirports = (airports: AirportModel[]) => {
  const res: AirportModel[] = []
  airports.forEach(airport => {
    if (res.some(a => !compareAirports(a, airport))) {
      return
    }
    res.push(airport)
  })

  return res
}

export const buildAirportFromInitial = (
  initial: BookFormModel | undefined,
  type: 'from' | 'to',
): AirportModel | null => {
  if (!initial) return null

  const lat = initial[`${type}PointLat`]
  const long = initial[`${type}PointLong`]

  return {
    address: initial[`${type}PointDetails`] || '',
    name: initial[`${type}PointName`] || '',
    latitude: lat?.toString() || '',
    longitude: long?.toString() || '',
    place_id: initial[`${type}PointPlaceId`] || '',
    city: {
      countryId: Number(initial.country?.id),
      latitude: initial.country?.latitude?.toString() || '',
      longitude: initial.country?.longitude?.toString() || '',
      countryName: initial.country?.name || '',
    },
    types: initial[`${type}PointTypes`] ? [initial[`${type}PointTypes`]] : [],
  } as AirportModel
}

export const useDebouncedSearch = (
  queryFn: (
    params: {
      countryId?: ID
      search?: string
      controller?: AbortController
    },
    callbacks?: QueryCallbacks<AirportModel[]> | undefined,
  ) => Promise<void>,
  resetFn: () => void,
  countryId: ID | undefined,
) => {
  const controllerRef = useRef<AbortController>(null)

  const handleSearch = useMemo(() => {
    return debounce(async (search: string) => {
      if (!search) return resetFn()
      if (search.length < 3) return

      if (controllerRef.current) {
        controllerRef.current.abort()
      }
      const controller = new AbortController()
      controllerRef.current = controller

      try {
        await queryFn({
          search,
          countryId: countryId || '',
          controller: controllerRef.current,
        })
      } catch (err) {
        if (axios.isCancel(err)) {
          return
        }

        throw err
      }
    }, 1000)
  }, [queryFn, countryId, resetFn])

  return handleSearch
}

export const mergeAirportOptions = ({
  initial,
  options,
  searchResults,
  selected,
  compareTo,
}: {
  selected: AirportModel | null
  searchResults: AirportModel[] | null
  options: AirportModel[]
  initial: AirportModel | null
  compareTo: { lat: number; long: number }
}) => {
  if (searchResults?.length) {
    return filterAirports(
      [...searchResults, initial, selected].filter(Boolean) as AirportModel[],
    ).filter(a =>
      compareAirports(a, {
        latitude: compareTo.lat,
        longitude: compareTo.long,
      }),
    )
  }

  return filterAirports(
    [selected, initial, ...options].filter(Boolean) as AirportModel[],
  ).filter(a =>
    compareAirports(a, {
      latitude: compareTo.lat,
      longitude: compareTo.long,
    }),
  )
}
